/**
 * Axiome & Snapa Roof Calculator — Rewritten
 * Reads from window.roofCalcData (pre-filled by Liquid section)
 * No external API calls, no tag-based filtering
 */

(function () {
  'use strict';

  var data = window.roofCalcData;
  if (!data) {
    console.warn('Roof calculator: no data found. Check Liquid section.');
    return;
  }

  // ─── State ───────────────────────────────────────────────────────────
  var state = {
    tag1: '',
    rfnm: 1,
    roofKey: '',
    roofName: '',
    colour: 'Clear',
    thickness: '10mm',
    sizeA: 0,
    sizeB: 0,
    sheets: 0,
    cartItems: []
  };


  // ─── Helpers ─────────────────────────────────────────────────────────

  function findLengthTier(tiers, measurementMm) {
    var sorted = tiers.slice().sort(function (a, b) { return a.maxLength - b.maxLength; });
    for (var i = 0; i < sorted.length; i++) {
      if (measurementMm <= sorted[i].maxLength) {
        return sorted[i].variantLabel;
      }
    }
    return null;
  }

  function findVariant(product, matchValues) {
    if (!product || !product.variants) return null;
    var target = matchValues.join(' / ');
    for (var i = 0; i < product.variants.length; i++) {
      if (product.variants[i].title === target) {
        return product.variants[i];
      }
    }
    for (var j = 0; j < product.variants.length; j++) {
      if (product.variants[j].title.indexOf(matchValues[0]) === 0) {
        return product.variants[j];
      }
    }
    return product.variants[0] || null;
  }

  /**
   * Find variant by a single option value appearing anywhere in the title.
   * Normalises both label and variant title by removing spaces and converting
   * commas to dots — handles French locale variant titles like "2,0 m" matching
   * against tier labels like "2.0m".
   */
  function findVariantByLabel(product, label) {
    if (!product || !product.variants) return null;
    var normLabel = label.replace(/\s/g, '').replace(/,/g, '.').toLowerCase();
    for (var i = 0; i < product.variants.length; i++) {
      var normTitle = product.variants[i].title.replace(/\s/g, '').replace(/,/g, '.').toLowerCase();
      if (normTitle.indexOf(normLabel) !== -1) {
        return product.variants[i];
      }
    }
    return null;
  }

  /**
   * Find Axiome sheet variant by matching BOTH the calculator's sheet width
   * AND the length tier. Required for products with width × length variant titles
   * (e.g. "690mm / 3000mm"). A plain substring match on tier label "3000mm"
   * would also match "1050mm / 3000mm" variants — this enforces 690mm width.
   *
   * For products without a width dimension (e.g. Snapa bars with titles like
   * "2.0m / White"), the width match will fail — in that case we fall back to
   * the plain findVariantByLabel behaviour so non-sheet products still work.
   */
  function findSheetVariant(product, tierLabel) {
    if (!product || !product.variants || !tierLabel) return null;
    var widthLabel = data.sheetWidthMm + 'mm';
    var normWidth = widthLabel.replace(/\s/g, '').replace(/,/g, '.').toLowerCase();
    var normTier = tierLabel.replace(/\s/g, '').replace(/,/g, '.').toLowerCase();

    // Check whether any variant on this product contains the sheet width
    // If yes, this is a multi-dimension sheet product → enforce width match
    // If no, this is a bar/single-dimension product → fall back to label-only match
    var hasWidthDimension = false;
    for (var k = 0; k < product.variants.length; k++) {
      var checkTitle = product.variants[k].title.replace(/\s/g, '').replace(/,/g, '.').toLowerCase();
      if (checkTitle.indexOf(normWidth) !== -1) {
        hasWidthDimension = true;
        break;
      }
    }

    for (var i = 0; i < product.variants.length; i++) {
      var normTitle = product.variants[i].title.replace(/\s/g, '').replace(/,/g, '.').toLowerCase();
      if (hasWidthDimension) {
        // Strict: must contain both width and tier
        if (normTitle.indexOf(normWidth) !== -1 && normTitle.indexOf(normTier) !== -1) {
          return product.variants[i];
        }
      } else {
        // Fallback: tier match only (same as findVariantByLabel)
        if (normTitle.indexOf(normTier) !== -1) {
          return product.variants[i];
        }
      }
    }
    return null;
  }

  function splitLength(tiers, measurementMm) {
    var sorted = tiers.slice().sort(function (a, b) { return a.maxLength - b.maxLength; });
    var results = [];
    for (var i = 0; i < sorted.length; i++) {
      if (measurementMm <= sorted[i].maxLength) {
        results.push({ variantLabel: sorted[i].variantLabel, quantity: 1 });
        return results;
      }
    }
    var longest = sorted[sorted.length - 1];
    var fullLengths = Math.floor(measurementMm / longest.maxLength);
    var remainder = measurementMm % longest.maxLength;
    if (fullLengths > 0) {
      results.push({ variantLabel: longest.variantLabel, quantity: fullLengths });
    }
    if (remainder > 0) {
      for (var j = 0; j < sorted.length; j++) {
        if (remainder <= sorted[j].maxLength) {
          results.push({ variantLabel: sorted[j].variantLabel, quantity: 1 });
          break;
        }
      }
    }
    return results;
  }

  function getOverride(rule) {
    var overrides = rule.roofTypeOverrides;
    if (typeof overrides === 'string') {
      try { overrides = JSON.parse(overrides); } catch (e) { return null; }
    }
    if (!overrides) return null;
    return overrides[state.roofKey] || null;
  }

  /**
   * Get the correct product from a rule's products list.
   * For rules with multiple products (e.g. Axiome sheets split by colour/thickness),
   * matches by SKU prefix pattern: AS[thickness][colourCode].
   * SKUs are never translated, so this works across all locales.
   * Colour codes: C = Clear, O = Opal, B = Bronze.
   */
  function getProduct(rule) {
    var products = rule.products;
    if (!products || products.length === 0) return null;
    if (products.length === 1) return products[0];
 
    // Map colour to SKU code
    var colourCode = {clear: 'C', opal: 'O', bronze: 'B'};
    var code = colourCode[state.colour.toLowerCase()] || '';
    var thicknessNum = parseInt(state.thickness) || 0; // "10mm" -> 10
 
    // Match by SKU prefix pattern: AS[thickness][colourCode]
    var skuPrefix = 'AS' + thicknessNum + code;
 
    for (var i = 0; i < products.length; i++) {
      if (products[i].variants && products[i].variants.length > 0) {
        var sku = products[i].variants[0].sku || '';
        if (sku.indexOf(skuPrefix) === 0) {
          return products[i];
        }
      }
    }
 
    return products[0];
  }


  // ─── Core calculation ────────────────────────────────────────────────

  function calculateKit() {
    state.cartItems = [];
    var rules = data.productRules;

    for (var r = 0; r < rules.length; r++) {
      var rule = rules[r];
      var override = getOverride(rule);
      var product = getProduct(rule);

      if (!override) continue;
      if (!product) continue;

      var formula = rule.qtyFormula;
      var variantDrivenBy = rule.variantDrivenBy;
      var quantity = 0;
      var variant = null;

      if (variantDrivenBy === 'size_b_length_tier') {
        var tierLabel = findLengthTier(rule.lengthTiers, state.sizeB);
        if (!tierLabel) {
          showMaxLengthMessage();
          return;
        }
        // Enforce sheet width match alongside length tier to prevent ambiguous
        // substring matches (tier "3000mm" matching both "690mm / 3000mm" and "1050mm / 3000mm")
        variant = findSheetVariant(product, tierLabel);

      } else if (variantDrivenBy === 'size_a_split_length') {
        var splits = splitLength(rule.lengthTiers, state.sizeA);
        for (var s = 0; s < splits.length; s++) {
          var splitVariant = findVariantByLabel(product, splits[s].variantLabel);
          if (splitVariant) {
            state.cartItems.push({
              rule: rule,
              product: product,
              variant: splitVariant,
              quantity: splits[s].quantity,
              editable: true
            });
          }
        }
        continue;

      } else if (variantDrivenBy === 'thickness_match') {
        variant = findVariantByLabel(product, state.thickness);

      } else if (variantDrivenBy === 'fixed') {
        if (rule.fixedVariantLabel) {
          variant = findVariantByLabel(product, rule.fixedVariantLabel);
        } else {
          variant = product.variants[0];
        }
      }

      if (!variant) continue;

      if (formula === 'ceil_a_div_700') {
        var divisor = data.sheetWidthMm + data.sheetGapMm;
        quantity = Math.ceil(state.sizeA / divisor);
        var multiplier = override.multiplier || 1;
        quantity = quantity * multiplier;
        state.sheets = Math.ceil(state.sizeA / divisor);

      } else if (formula === 'sheets_minus') {
        var sheetCount = state.sheets;
        var apexMultiplier = override.multiplier || 1;
        quantity = (sheetCount * apexMultiplier) - (override.sheets_minus || 0);
        if (quantity < 0) quantity = 0;

      } else if (formula === 'fixed') {
        quantity = override.fixed_qty || 0;

      } else if (formula === 'ceil_a_div_2100') {
        quantity = Math.ceil(state.sizeA / 2100);
        quantity = quantity * (override.multiplier || 1);

      } else if (formula === 'ceil_measurement_div_10000') {
        var measurement = 0;
        if (override.measurement === 'a') {
          measurement = state.sizeA;
        } else if (override.measurement === 'a_plus_b') {
          measurement = state.sizeA + state.sizeB;
        } else if (override.measurement === 'b_times_2') {
          measurement = state.sizeB * 2;
        }
        quantity = Math.ceil(measurement / 10000);
        if (quantity < 1) quantity = 1;
      }

      if (quantity > 0) {
        state.cartItems.push({
          rule: rule,
          product: product,
          variant: variant,
          quantity: quantity,
          editable: true
        });
      }
    }

    renderKitList();
  }


  // ─── Editable variant list (Step 7 — new) ─────────────────────────

  function renderKitList() {
    var container = document.querySelector('.dkit .kit-list');
    var mcontainer = document.querySelector('.mkit .kit-list');
    if (!container) {
      console.warn('Roof calculator: .kit-list container not found');
      return;
    }

    var html = '';
    var mhtml = '';
    var total = 0;
    var totalCompareAt = 0;
    var totalSaved = 0;

    for (var i = 0; i < state.cartItems.length; i++) {
      var item = state.cartItems[i];
      var price = item.variant.price / 100;
      var compareAt = item.variant.compareAtPrice ? item.variant.compareAtPrice / 100 : null;
      var lineTotal = price * item.quantity;
      var lineCompareAt = compareAt ? compareAt * item.quantity : lineTotal;
      var lineSaving = compareAt ? (compareAt - price) * item.quantity : 0;
      total += lineTotal;
      totalCompareAt += lineCompareAt;
      totalSaved += lineSaving;

      var image = '';
      if (item.product && item.product.images && item.product.images.length > 0) {
        image = item.product.images[0];
      }

      // Desktop layout
      html += '<div class="kit-item" data-index="' + i + '">';
      html += '  <div class="kit-item-image">';
      if (image) {
        html += '    <img src="' + image + '" alt="' + (item.product ? item.product.title : '') + '">';
      }
      html += '  </div>';
      html += '  <div class="kit-item-details">';
      html += '    <div class="kit-item-title">' + (item.product ? item.product.title : item.rule.entryLabel) + '</div>';
      html += '    <div class="kit-item-variant">' + item.variant.title + '</div>';
      html += '    <div class="kit-item-compare">';
      if (compareAt && compareAt > price) {
        html += '    <span class="kit-price-sale">' + formatMoney(price) + '</span>';
        html += '    <span class="kit-price-compare">' + formatMoney(compareAt) + '</span>';
      } else {
        html += '    <span>' + formatMoney(price) + '</span>';
      }
      html += '  </div>';
      html += '  </div>';
      html += '  <div class="kit-item-qty">';
      html += '    <button class="kit-qty-minus" data-index="' + i + '">−</button>';
      html += '    <input type="number" class="kit-qty-input" data-index="' + i + '" value="' + item.quantity + '" min="0">';
      html += '    <button class="kit-qty-plus" data-index="' + i + '">+</button>';
      html += '  </div>';
      html += '  <div class="kit-item-price">' + formatMoney(lineTotal) + '</div>';
      html += '</div>';

      // Mobile layout
      mhtml += '<div class="kit-item" data-index="' + i + '">';
      mhtml += '  <div class="w-100"><div class="w-25">';
      mhtml += '  <div class="kit-item-image">';
      if (image) {
        mhtml += '    <img src="' + image + '" alt="' + (item.product ? item.product.title : '') + '">';
      }
      mhtml += '  </div>';
      mhtml += '  </div><div class="w-75">';
      mhtml += '  <div class="kit-item-details">';
      mhtml += '    <div class="kit-item-title">' + (item.product ? item.product.title : item.rule.entryLabel) + '</div>';
      mhtml += '    <div class="kit-item-variant">' + item.variant.title + '</div>';
      mhtml += '    <div class="kit-item-compare">';
      if (compareAt && compareAt > price) {
        mhtml += '    <span class="kit-price-sale">' + formatMoney(price) + '</span>';
        mhtml += '    <span class="kit-price-compare">' + formatMoney(compareAt) + '</span>';
      } else {
        mhtml += '    <span>' + formatMoney(price) + '</span>';
      }
      mhtml += '  </div>';
      mhtml += '  </div>';
      mhtml += '  <div class="kit-item-qty">';
      mhtml += '    <button class="kit-qty-minus" data-index="' + i + '">−</button>';
      mhtml += '    <input type="number" class="kit-qty-input" data-index="' + i + '" value="' + item.quantity + '" min="0">';
      mhtml += '    <button class="kit-qty-plus" data-index="' + i + '">+</button>';
      mhtml += '  </div>';
      mhtml += '  </div></div>';
      mhtml += '</div>';
    }

    container.innerHTML = html;
    mcontainer.innerHTML = mhtml;

    // Update total in both desktop and mobile
    $('.kitcart .total span').html(formatMoney(total));

    // Show savings
    if (totalSaved > 0) {
      var ysav = 'You saved ';
      if (window.location.pathname.indexOf('fr-fr') === 1) { ysav = 'Vous avez enregistré '; }
      $('.kitcart .saved').show().html(ysav + formatMoney(totalSaved) + '!');
    } else {
      $('.kitcart .saved').hide();
    }

    // Show the kit list step
    showStep('kit');

    // Bind events
    bindKitListEvents();
  }

  function bindKitListEvents() {
    document.querySelectorAll('.kit-qty-minus').forEach(function (btn) {
      btn.addEventListener('click', function () {
        var idx = parseInt(this.getAttribute('data-index'));
        if (state.cartItems[idx].quantity > 0) {
          state.cartItems[idx].quantity--;
          renderKitList();
        }
      });
    });

    document.querySelectorAll('.kit-qty-plus').forEach(function (btn) {
      btn.addEventListener('click', function () {
        var idx = parseInt(this.getAttribute('data-index'));
        state.cartItems[idx].quantity++;
        renderKitList();
      });
    });

    document.querySelectorAll('.kit-qty-input').forEach(function (input) {
      input.addEventListener('change', function () {
        var idx = parseInt(this.getAttribute('data-index'));
        var val = parseInt(this.value) || 0;
        if (val < 0) val = 0;
        state.cartItems[idx].quantity = val;
        renderKitList();
      });
    });

    document.querySelectorAll('.kit-add-to-cart').forEach(function (btn) {
      btn.addEventListener('click', function () {
        document.querySelectorAll('.kit-add-to-cart').forEach(function (b) {
          b.setAttribute('disabled', 'disabled');
          if(window.location.pathname.indexOf('fr-fr') == 1){ b.textContent = 'Ajout au panier...'; }else{ b.textContent = 'Adding to cart...'; }
          // b.textContent = 'Adding to cart...';
        });
        addToCart();
      });
    });
  }

  function formatMoney(amount) {
    var currency = data.currency || {};
    var locale = currency.locale || 'en-GB';
    var code = currency.code || 'GBP';
    try {
      return new Intl.NumberFormat(locale, {
        style: 'currency',
        currency: code
      }).format(amount);
    } catch (e) {
      return (currency.symbol || '£') + amount.toFixed(2);
    }
  }


  // ─── Cart submission ──────────────────────────────────────────────

  function addToCart() {
    var items = [];

    for (var i = 0; i < state.cartItems.length; i++) {
      var item = state.cartItems[i];
      if (item.quantity < 1) continue;

      items.push({
        quantity: item.quantity,
        id: item.variant.id,
        properties: {
          '_ROOF TYPE': state.roofName,
          '_COLOUR': state.colour,
          '_LENGTH A': state.sizeA,
          '_LENGTH B': state.sizeB
        }
      });
    }

    if (items.length === 0) {
      alert('No items to add. Please check your quantities.');
      document.querySelectorAll('.kit-add-to-cart').forEach(function (btn) {
        btn.removeAttribute('disabled');
        btn.textContent = 'Add full kit to cart';
      });
      return;
    }

    // Reverse so items appear in correct order in cart
    items.reverse();

    jQuery.post('/cart/add.js', {
      items: items
    }).then(function () {
      if(window.location.pathname.indexOf('fr-fr') == 1){ window.location.href = '/fr-fr/cart'; }else{ window.location.href = '/cart'; }
    }).fail(function () {
      alert('There was an error adding to cart. Please try again.');
      document.querySelectorAll('.kit-add-to-cart').forEach(function (btn) {
        btn.removeAttribute('disabled');
        btn.textContent = 'Add full kit to cart';
      });
    });
  }


  // ─── Max length message ───────────────────────────────────────────

  function showMaxLengthMessage() {
    var msg = data.maxLengthMessage || 'This size is too long, please get free advice on Live Chat';
    alert(msg);
  }


  // ─── UI step management ───────────────────────────────────────────

  function showStep(step) {
    $('.rooftype, .roofsheet, .roofsize, .kit-list-wrapper').hide();

    if (step === 'rooftype') {
      $('.rooftype').show();
      updateStepIndicators('wtor');
    } else if (step === 'roofsheet') {
      $('.roofsheet').show();
      updateStepIndicators('yrc');
    } else if (step === 'roofsize') {
      $('.roofsize').show();
      updateStepIndicators('cyrs');
    } else if (step === 'kit') {
      $('.kit-list-wrapper').show();
      updateStepIndicators('kit');
    }
  }

  function updateStepIndicators(currentStep) {
    $(".roofcal .number").removeClass("current").removeClass("complete");
    var steps = ['wtor', 'yrc', 'cyrs', 'kit'];
    var currentIdx = steps.indexOf(currentStep);
    for (var i = 0; i < steps.length; i++) {
      if (i < currentIdx) {
        $(".roofcal .number." + steps[i]).addClass("complete");
      } else if (i === currentIdx) {
        $(".roofcal .number." + steps[i]).addClass("current");
      }
    }
  }


  // ─── Event bindings ───────────────────────────────────────────────

  $(".roofcal .roofimg").click(function () {
    var roofIndex = parseInt($(this).attr('key')) - 1;
    var roofType = data.roofTypes[roofIndex];
    if (!roofType) return;

    state.roofKey = roofType.key;
    state.roofName = roofType.name;

    state.tag1 = $(this).attr('title');
    state.rfnm = $(this).attr('key');
    var image = $(this).find('img').attr('src');
    var img = state.tag1.replaceAll('– ', '');
    img = img.replaceAll(' ', '_') + '_' + state.rfnm;
    image = image.replace(img, 'Axiome_' + img + '_' + state.colour);
    $('.roofsheet img').attr('src', image);
    $('.roofsize img').attr('src', image);
    $('.qimg img').attr('src', image);
    $('.roofsheet img').attr('key', $(this).find('img').attr('src'));

    $('select[name="colour1"]').val('Clear').trigger('change');

    showStep('roofsheet');
  });

  $('.srtype').click(function () {
    state.roofKey = '';
    state.roofName = '';
    showStep('rooftype');
  });

  $('select[name="colour1"]').change(function () {
    state.colour = $(this).val();
    const text = this.options[this.selectedIndex].text;
    $('.quote .clr').text(text);

    var img = state.tag1.replaceAll('– ', '');
    img = img.replaceAll(' ', '_') + '_' + state.rfnm;
    var crtimg = $(".roofsheet img").attr("key");
    $(".roofsheet img, .roofsize img, .qimg img").attr("src", crtimg.replace(img, "Axiome_" + img + "_" + state.colour));
  });

  $('.thickness').change(function () {
    state.thickness = $(this).val();
    $('.quote .thick').text(state.thickness);
  });

  $('.rfsize').click(function () {
    if (!state.roofKey) {
      alert('Please select a Roof Type');
      return false;
    }
    if (!state.colour) {
      alert('Please select a Colour');
      return false;
    }
    showStep('roofsize');
  });

  $(".roofcal .yrc").click(function () {
    if (!state.roofKey) return false;
    showStep('roofsheet');
  });

  $('.ckit').click(function () {
    $(this).attr('disabled', 'disabled');

    state.sizeA = parseInt($('.roofinput input[name="lengtha"]').val()) || 0;
    state.sizeB = parseInt($('.roofinput input[name="lengthb"]').val()) || 0;

    if (state.sizeA < 99 || state.sizeB < 99) {
      alert(data.minSizeMessage || 'Please enter valid length and width');
      $(this).attr('disabled', false);
      return false;
    }
    $('.quote .sizea span').text(state.sizeA);
    $('.quote .sizeb span').text(state.sizeB);

    var maxAvailableLength = 6000;
    var longestTier = 0;
    for (var r = 0; r < data.productRules.length; r++) {
      var tiers = data.productRules[r].lengthTiers;
      for (var t = 0; t < tiers.length; t++) {
        if (tiers[t].maxLength > longestTier) {
          longestTier = tiers[t].maxLength;
        }
      }
    }
    if (longestTier > 0) maxAvailableLength = longestTier;

    if (state.sizeB > maxAvailableLength) {
      showMaxLengthMessage();
      $(this).attr('disabled', false);
      return false;
    }

    calculateKit();

    $(this).attr('disabled', false);
  });

  $(".roofinput input").change(function () {
    $(".ckit").attr("disabled", false);
  });

})();